<template>
  <div class="vehicle-application-manager">
    <!-- Header with Actions -->
    <div class="header-actions">
      <div class="verification-status">
        <template v-if="!loading">
          <div v-if="!verified" class="text-red-600 font-semibold">
            ⚠ Not verified yet
          </div>
          <div v-else class="text-green-600">
            ✓ Verified by {{ verified.name }} at
            {{ verified.date | dateWithHours }}
            <button
              v-if="userCanEditProducts"
              @click="removeVerification(verified.id)"
              class="text-red-500 ml-2 underline"
            >
              Remove
            </button>
          </div>
          <button
            v-if="userCanEditProducts"
            @click="markVerified"
            class="btn-dark-button mt-2"
          >
            Mark as Verified
          </button>
        </template>
      </div>

      <div class="action-buttons">
        <CopyFromUnificado
          v-if="userCanEditProducts"
          @added="loadApplications"
          :unified="unified"
        ></CopyFromUnificado>
        <button
          v-if="!isAddMode && userCanEditProducts"
          @click="isAddMode = true"
          class="btn-success-button"
        >
          + Add Applications
        </button>
        <button
          v-else-if="isAddMode && userCanEditProducts"
          @click="isAddMode = false"
          class="btn-dark-button"
        >
          Close Add Mode
        </button>
      </div>
    </div>

    <!-- Add Mode: Vehicle Selector -->
    <VehicleSelector
      v-if="isAddMode"
      :unified="unified"
      :existing-applications="applications"
      @applications-added="onApplicationsAdded"
      @close="isAddMode = false"
    />

    <!-- View Mode: Applications Table -->
    <ApplicationsTable
      v-else
      :unified="unified"
      :applications="applications"
      :loading="loading"
      @refresh="loadApplications"
    />
  </div>
</template>

<script>
import CopyFromUnificado from "@/components/product/CopyFromUnificado";
import VehicleSelector from "./applications/VehicleSelector.vue";
import ApplicationsTable from "./applications/ApplicationsTable.vue";
import {
  markApplicationVerified,
  removeVerification as removeVerificationApi,
  getApplications
} from "@/api/store/ApplicationsService";

export default {
  name: "VehicleApplicationManager",
  components: {
    CopyFromUnificado,
    VehicleSelector,
    ApplicationsTable
  },
  props: {
    unified: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isAddMode: false,
      applications: [],
      verified: null,
      loading: false
    };
  },
  computed: {
    userCanEditProducts() {
      return this.$store.getters["auth/userHasPermission"]("can_edit_products");
    }
  },
  mounted() {
    this.loadApplications();
  },
  methods: {
    async loadApplications() {
      this.loading = true;
      try {
        const response = await getApplications(this.unified);
        this.applications = response.applications || [];
        this.verified = response.verification;
      } catch (error) {
        this.$toast.error("Error loading applications");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async markVerified() {
      if (confirm("Mark these applications as verified?")) {
        try {
          const res = await markApplicationVerified(this.unified);
          this.verified = res;
          this.$toast.success("Applications marked as verified");
        } catch (error) {
          this.$toast.error("Error marking as verified");
        }
      }
    },
    async removeVerification(id) {
      try {
        const res = await removeVerificationApi(this.unified, id);
        this.verified = res;
        this.$toast.success("Verification removed");
      } catch (error) {
        this.$toast.error("Error removing verification");
      }
    },
    onApplicationsAdded() {
      this.isAddMode = false;
      this.loadApplications();
    }
  }
};
</script>

<style lang="scss" scoped>
.vehicle-application-manager {
  padding: 1rem;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 0.5rem;
}

.verification-status {
  flex: 1;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}
</style>
